import {
  FOCUS_SETTLE_MS,
  placementPolicyFor,
  qualityPolicyFor,
  type InternalQualityPolicy,
} from "./policy";
import { analyzeFrame, type InternalFrameAnalysis } from "./quality";
import { captureModeFor } from "./target";
import {
  FingerprintCaptureError,
  type CaptureProgress,
  type FingerprintCaptureOptions,
  type GuidanceCode,
} from "./types";

type CapturePhase =
  | "placement"
  | "focusing"
  | "detail"
  | "capturing"
  | "enhancing";

type SessionProgress = CaptureProgress & {
  analysis?: InternalFrameAnalysis;
  policy?: Readonly<InternalQualityPolicy>;
  focusStrategy?: string;
  phase?: CapturePhase;
};

type ProgressHandler = (progress: SessionProgress) => void;

interface ApprovedCaptureFrame {
  frame: HTMLCanvasElement;
  analysis: InternalFrameAnalysis;
}

type ExtendedCameraCapabilities = MediaTrackCapabilities & {
  exposureMode?: string[];
  focusMode?: string[];
};

type ExtendedSupportedConstraints = MediaTrackSupportedConstraints & {
  exposureMode?: boolean;
  focusMode?: boolean;
  pointsOfInterest?: boolean;
};

type ExtendedCameraConstraints = MediaTrackConstraintSet & {
  exposureMode?: string;
  focusMode?: string;
  pointsOfInterest?: Array<{ x: number; y: number }>;
};

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else {
        reject(
          new FingerprintCaptureError(
            "PROCESSING_FAILED",
            "The browser could not encode the fingerprint image.",
          ),
        );
      }
    }, "image/png");
  });
}

const VIDEO_FRAME_WATCHDOG_MS = 180;

export function waitForVideoFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    let callbackId: number | undefined;
    const hasVideoFrameCallback =
      typeof video.requestVideoFrameCallback === "function";

    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(watchdog);
      if (
        callbackId !== undefined &&
        typeof video.cancelVideoFrameCallback === "function"
      ) {
        video.cancelVideoFrameCallback(callbackId);
      }
      resolve();
    };

    // Some Android browsers expose requestVideoFrameCallback but can stop
    // delivering it while the camera pipeline is busy. Never let capture wait
    // forever after quality approval.
    const watchdog = window.setTimeout(
      finish,
      hasVideoFrameCallback ? VIDEO_FRAME_WATCHDOG_MS : 70,
    );
    if (hasVideoFrameCallback) {
      callbackId = video.requestVideoFrameCallback(finish);
    }
  });
}

export function shouldReplaceApprovedFrame(
  analysis: InternalFrameAnalysis,
  bestScore: number,
): boolean {
  return analysis.passed && analysis.score > bestScore;
}

function cameraError(error: unknown): FingerprintCaptureError {
  if (error instanceof FingerprintCaptureError) return error;
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "SecurityError") {
      return new FingerprintCaptureError(
        "PERMISSION_DENIED",
        "Camera permission was denied.",
        true,
        { cause: error },
      );
    }
    if (
      error.name === "NotFoundError" ||
      error.name === "NotReadableError" ||
      error.name === "AbortError"
    ) {
      return new FingerprintCaptureError(
        "CAMERA_UNAVAILABLE",
        "A usable camera is not available.",
        true,
        { cause: error },
      );
    }
  }

  return new FingerprintCaptureError(
    "PROCESSING_FAILED",
    "Fingerprint capture failed unexpectedly.",
    true,
    { cause: error },
  );
}

export class CameraCaptureSession {
  private stream?: MediaStream;
  private animationFrame?: number;
  private timeout?: number;
  private stopped = false;
  private videoTrack?: MediaStreamTrack;
  private cameraCapabilities?: ExtendedCameraCapabilities;
  private focusStrategy = "browser auto";
  private lastRefocusAt = 0;
  private refocusInFlight = false;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly options: Required<
      Pick<
        FingerprintCaptureOptions,
        | "timeoutMs"
        | "stableDurationMs"
        | "burstFrames"
        | "cameraFacing"
        | "finger"
      >
    >,
    private readonly signal: AbortSignal,
    private readonly onProgress: ProgressHandler,
    private readonly onReady: (width: number, height: number) => void,
  ) {}

  async run(): Promise<Blob> {
    if (!window.isSecureContext) {
      throw new FingerprintCaptureError(
        "INSECURE_CONTEXT",
        "Camera capture requires HTTPS or localhost.",
        false,
      );
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new FingerprintCaptureError(
        "UNSUPPORTED_DEVICE",
        "This browser does not support camera capture.",
        false,
      );
    }
    if (this.signal.aborted) {
      throw new FingerprintCaptureError(
        "CAPTURE_CANCELLED",
        "Fingerprint capture was cancelled.",
      );
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: this.options.cameraFacing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 24, min: 15 },
        },
      });

      await this.configureGuideFocus(this.stream);
      this.video.srcObject = this.stream;
      this.video.muted = true;
      this.video.playsInline = true;
      await new Promise<void>((resolve, reject) => {
        if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
          resolve();
          return;
        }
        this.video.addEventListener("loadedmetadata", () => resolve(), {
          once: true,
        });
        this.video.addEventListener(
          "error",
          () => reject(new Error("Camera preview failed.")),
          { once: true },
        );
      });
      await this.video.play();

      if (this.video.videoWidth < 480 || this.video.videoHeight < 480) {
        throw new FingerprintCaptureError(
          "UNSUPPORTED_DEVICE",
          "The available camera resolution is too low for fingerprint capture.",
          false,
        );
      }

      this.onReady(this.video.videoWidth, this.video.videoHeight);
      const approvedFrame = await this.waitUntilQualityPasses();
      this.emit("CAPTURING", 1, undefined, undefined, "capturing");
      const bestFrame = await this.captureBestFrame(approvedFrame);
      return await canvasToBlob(bestFrame);
    } catch (error) {
      throw cameraError(error);
    } finally {
      this.stop();
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.timeout !== undefined) {
      clearTimeout(this.timeout);
    }
    this.stream?.getTracks().forEach((track) => track.stop());
    this.video.srcObject = null;
  }

  private emit(
    guidance: GuidanceCode,
    stableProgress: number,
    analysis?: InternalFrameAnalysis,
    policy?: Readonly<InternalQualityPolicy>,
    phase: CapturePhase = "placement",
  ): void {
    this.onProgress({
      guidance,
      stableProgress,
      analysis,
      policy,
      focusStrategy: this.focusStrategy,
      phase,
    });
  }

  private async configureGuideFocus(stream: MediaStream): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track || typeof track.getCapabilities !== "function") return;

    this.videoTrack = track;
    this.cameraCapabilities =
      track.getCapabilities() as ExtendedCameraCapabilities;
    const supported =
      navigator.mediaDevices.getSupportedConstraints() as ExtendedSupportedConstraints;
    const focusModes = this.cameraCapabilities.focusMode ?? [];
    const focusMode = focusModes.includes("continuous")
      ? "continuous"
      : focusModes.includes("single-shot")
        ? "single-shot"
        : undefined;
    const constraints: ExtendedCameraConstraints = {};

    if (focusMode && supported.focusMode) constraints.focusMode = focusMode;
    if (supported.pointsOfInterest) {
      constraints.pointsOfInterest = [{ x: 0.5, y: 0.5 }];
    }
    if (
      supported.exposureMode &&
      this.cameraCapabilities.exposureMode?.includes("continuous")
    ) {
      constraints.exposureMode = "continuous";
    }

    if (Object.keys(constraints).length === 0) return;

    try {
      await track.applyConstraints({ advanced: [constraints] });
      const usesRoi = Boolean(constraints.pointsOfInterest);
      this.focusStrategy = usesRoi
        ? "guide ROI + auto-refocus"
        : focusMode
          ? focusMode + " autofocus"
          : "browser auto";
    } catch {
      if (!focusMode) return;
      try {
        await track.applyConstraints({
          advanced: [
            { focusMode } as ExtendedCameraConstraints,
          ],
        });
        this.focusStrategy = focusMode + " autofocus";
      } catch {
        this.focusStrategy = "browser auto";
      }
    }
  }

  private refocusOnGuide(now: number): boolean {
    if (
      !this.videoTrack ||
      !this.cameraCapabilities ||
      this.refocusInFlight ||
      now - this.lastRefocusAt < 2_500
    ) {
      return false;
    }

    this.lastRefocusAt = now;
    this.refocusInFlight = true;
    const supported =
      navigator.mediaDevices.getSupportedConstraints() as ExtendedSupportedConstraints;
    const modes = this.cameraCapabilities.focusMode ?? [];
    const focusMode = modes.includes("single-shot")
      ? "single-shot"
      : modes.includes("continuous")
        ? "continuous"
        : undefined;
    const constraints: ExtendedCameraConstraints = {};
    if (focusMode && supported.focusMode) constraints.focusMode = focusMode;
    if (supported.pointsOfInterest) {
      constraints.pointsOfInterest = [{ x: 0.5, y: 0.5 }];
    }

    void (async () => {
      try {
        if (Object.keys(constraints).length > 0) {
          await this.videoTrack?.applyConstraints({
            advanced: [constraints],
          });
        }
      } catch {
        // Continue with the browser's own autofocus when ROI refocus is rejected.
      } finally {
        this.refocusInFlight = false;
      }
    })();
    return true;
  }

  private waitUntilQualityPasses(): Promise<ApprovedCaptureFrame> {
    const captureMode = captureModeFor(this.options.finger);
    const placementPolicy = placementPolicyFor(captureMode);
    const detailPolicy = qualityPolicyFor(captureMode);
    const aspect = this.video.videoHeight / this.video.videoWidth;

    const createSurface = (analysisWidth: number) => {
      const canvas = document.createElement("canvas");
      canvas.width = analysisWidth;
      canvas.height = Math.max(1, Math.round(analysisWidth * aspect));
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        throw new FingerprintCaptureError(
          "PROCESSING_FAILED",
          "Canvas analysis is unavailable.",
        );
      }
      return { canvas, context };
    };

    let placementSurface: ReturnType<typeof createSurface>;
    let detailSurface: ReturnType<typeof createSurface>;
    let approvedFrame: HTMLCanvasElement;
    let approvedContext: CanvasRenderingContext2D;
    try {
      placementSurface = createSurface(placementPolicy.analysisWidth);
      detailSurface = createSurface(detailPolicy.analysisWidth);
      approvedFrame = document.createElement("canvas");
      approvedFrame.width = this.video.videoWidth;
      approvedFrame.height = this.video.videoHeight;
      const context = approvedFrame.getContext("2d");
      if (!context) {
        throw new FingerprintCaptureError(
          "PROCESSING_FAILED",
          "Canvas capture is unavailable.",
        );
      }
      approvedContext = context;
    } catch (error) {
      return Promise.reject(error);
    }

    const readFrame = (surface: ReturnType<typeof createSurface>): ImageData => {
      surface.context.drawImage(
        this.video,
        0,
        0,
        surface.canvas.width,
        surface.canvas.height,
      );
      return surface.context.getImageData(
        0,
        0,
        surface.canvas.width,
        surface.canvas.height,
      );
    };

    return new Promise((resolve, reject) => {
      let phase: CapturePhase = "placement";
      let previousLuminance: Uint8Array | undefined;
      let placementStableSince: number | undefined;
      let lastPlacementAt = 0;
      let lastDetailAt = 0;
      let focusStartedAt = 0;

      const cancel = () => {
        reject(
          new FingerprintCaptureError(
            "CAPTURE_CANCELLED",
            "Fingerprint capture was cancelled.",
          ),
        );
      };
      this.signal.addEventListener("abort", cancel, { once: true });
      this.timeout = window.setTimeout(() => {
        reject(
          new FingerprintCaptureError(
            "QUALITY_TIMEOUT",
            "A clear fingerprint could not be captured in time.",
          ),
        );
      }, this.options.timeoutMs);

      const finish = (analysis: InternalFrameAnalysis) => {
        this.signal.removeEventListener("abort", cancel);
        if (this.timeout !== undefined) clearTimeout(this.timeout);
        // Preserve the exact full-resolution camera frame that passed the
        // detail policy. Later burst frames may replace it only if they also
        // pass, so autofocus changes cannot turn a valid capture blurry.
        approvedContext.drawImage(
          this.video,
          0,
          0,
          approvedFrame.width,
          approvedFrame.height,
        );
        resolve({ frame: approvedFrame, analysis });
      };

      const tick = (now: number) => {
        if (this.stopped || this.signal.aborted) return;

        if (phase === "focusing") {
          if (now - focusStartedAt >= FOCUS_SETTLE_MS) {
            phase = "detail";
          } else {
            this.animationFrame = requestAnimationFrame(tick);
            return;
          }
        }

        if (phase === "placement") {
          const interval = 1000 / placementPolicy.analysisFps;
          if (now - lastPlacementAt >= interval) {
            lastPlacementAt = now;
            const analysis = analyzeFrame(
              readFrame(placementSurface),
              placementPolicy,
              previousLuminance,
              captureMode,
              false,
            );
            previousLuminance = analysis.luminanceSample;

            if (analysis.passed) {
              placementStableSince ??= now;
              const progress = Math.min(
                1,
                (now - placementStableSince) / this.options.stableDurationMs,
              );
              this.emit(
                "READY",
                progress,
                analysis,
                placementPolicy,
                "placement",
              );
              if (progress >= 1) {
                phase = "focusing";
                focusStartedAt = now;
                placementStableSince = undefined;
                this.refocusOnGuide(now);
                this.emit(
                  "HOLD_STILL",
                  1,
                  analysis,
                  placementPolicy,
                  "focusing",
                );
              }
            } else {
              placementStableSince = undefined;
              this.emit(
                analysis.guidance,
                0,
                analysis,
                placementPolicy,
                "placement",
              );
            }
          }
        } else if (phase === "detail") {
          const detailInterval = 1000 / detailPolicy.analysisFps;
          if (now - lastDetailAt < detailInterval) {
            this.animationFrame = requestAnimationFrame(tick);
            return;
          }
          lastDetailAt = now;
          const analysis = analyzeFrame(
            readFrame(detailSurface),
            detailPolicy,
            previousLuminance,
            captureMode,
            true,
          );
          previousLuminance = analysis.luminanceSample;
          this.emit(
            analysis.guidance,
            analysis.passed ? 1 : 0,
            analysis,
            detailPolicy,
            "detail",
          );

          if (analysis.passed) {
            finish(analysis);
            return;
          }

          if (
            analysis.guidance === "IMAGE_BLURRY" ||
            analysis.guidance === "RIDGES_NOT_VISIBLE"
          ) {
            if (this.refocusOnGuide(now)) {
              phase = "focusing";
              focusStartedAt = now;
            }
          } else {
            phase = "placement";
            placementStableSince = undefined;
          }
        }

        this.animationFrame = requestAnimationFrame(tick);
      };

      this.animationFrame = requestAnimationFrame(tick);
    });
  }
  private async captureBestFrame(
    approved: ApprovedCaptureFrame,
  ): Promise<HTMLCanvasElement> {
    const width = this.video.videoWidth;
    const height = this.video.videoHeight;
    const frame = document.createElement("canvas");
    frame.width = width;
    frame.height = height;
    const frameContext = frame.getContext("2d", {
      willReadFrequently: true,
    });
    if (!frameContext) {
      throw new FingerprintCaptureError(
        "PROCESSING_FAILED",
        "Canvas capture is unavailable.",
      );
    }

    const captureMode = captureModeFor(this.options.finger);
    const policy = qualityPolicyFor(captureMode);
    const analysisWidth = policy.analysisWidth;
    const analysisHeight = Math.round((height / width) * analysisWidth);
    const analysisCanvas = document.createElement("canvas");
    analysisCanvas.width = analysisWidth;
    analysisCanvas.height = analysisHeight;
    const analysisContext = analysisCanvas.getContext("2d", {
      willReadFrequently: true,
    });
    if (!analysisContext) {
      throw new FingerprintCaptureError(
        "PROCESSING_FAILED",
        "Canvas analysis is unavailable.",
      );
    }

    const best = approved.frame;
    const bestContext = best.getContext("2d");
    if (!bestContext) {
      throw new FingerprintCaptureError(
        "PROCESSING_FAILED",
        "Canvas capture is unavailable.",
      );
    }

    let bestScore = approved.analysis.score;
    let previousLuminance = approved.analysis.luminanceSample;
    const burstFrames =
      captureMode === "four-fingers"
        ? Math.min(3, this.options.burstFrames)
        : this.options.burstFrames;
    for (let index = 1; index < burstFrames; index += 1) {
      if (this.signal.aborted) {
        throw new FingerprintCaptureError(
          "CAPTURE_CANCELLED",
          "Fingerprint capture was cancelled.",
        );
      }
      await waitForVideoFrame(this.video);
      frameContext.drawImage(this.video, 0, 0, width, height);
      analysisContext.drawImage(
        this.video,
        0,
        0,
        analysisWidth,
        analysisHeight,
      );
      const image = analysisContext.getImageData(
        0,
        0,
        analysisWidth,
        analysisHeight,
      );
      const analysis = analyzeFrame(
        image,
        policy,
        previousLuminance,
        captureMode,
      );
      previousLuminance = analysis.luminanceSample;
      if (shouldReplaceApprovedFrame(analysis, bestScore)) {
        bestScore = analysis.score;
        bestContext.drawImage(frame, 0, 0);
      }
    }

    return best;
  }
}
